import { zodTransformFirestoreTimestampToDate } from "../../../utils/zodUtils.mjs";
import { deepEqual } from "../../../utils/tools.mjs";
import { zodFlavorId } from "../../ids.mjs";
import { z as z$1 } from "zod";

// src/models/vendors/bridge/consultSyncTargets.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
/** Bridge consult vendor target key: service eligibility ensure. */
const BRIDGE_SYNC_TARGET_SERVICE_ELIGIBILITY = "serviceEligibility";
/** Bridge consult vendor target key: insured service ensure. */
const BRIDGE_SYNC_TARGET_SERVICE = "service";
/** Bridge consult vendor target key: admin Note + Bill Service after visit. */
const BRIDGE_SYNC_TARGET_SERVICE_BILL = "serviceBill";
/** Bridge consult vendor target key: payment ensure (syncId-keyed multi-map). */
const BRIDGE_SYNC_TARGET_PAYMENTS = "payments";
/**
* Payment registration kind on binding.target — v1 uses estimate_capture only.
* Reserved for later: settling collect/refund kinds.
*/
const bridgePaymentKindSchema = z$1.enum([
	"estimate_capture",
	"settling_collect",
	"settling_refund"
]);
/** Bridge Payment type on binding.target and observed L4. */
const bridgePaymentTypeSchema = z$1.enum(["PATIENT", "ADJUSTMENT"]);
/**
* Complete Bridge payment target on consultations/vendors/bridge binding.target.
* Built in platform-coord from Stripe L4 facts + service pointer.
* Bridge I3 reads this only — no consultation document reads.
*/
const bridgePaymentVendorTargetSchema = z$1.object({
	paymentKind: bridgePaymentKindSchema,
	customerId: zodFlavorId(),
	consultationId: zodFlavorId(),
	serviceId: zodFlavorId(),
	transactionId: z$1.string().min(1),
	amount: z$1.number().int(),
	paidAt: firestoreDate,
	type: bridgePaymentTypeSchema
});
/**
* Complete Bridge service-eligibility target on consultations/vendors/bridge.
* Bridge I3 reads this only — no consultation document reads.
*/
const bridgeServiceEligibilityVendorTargetSchema = z$1.object({
	customerId: zodFlavorId(),
	consultationId: zodFlavorId(),
	policyId: zodFlavorId(),
	policyVendorNativeId: zodFlavorId(),
	dateOfService: z$1.string().min(1),
	state: z$1.string().min(1),
	serviceTypeId: z$1.string().min(1),
	reservedSlotKey: z$1.string().min(1),
	providerId: zodFlavorId().optional()
});
/**
* Complete Bridge service target on consultations/vendors/bridge.
* Built in platform-coord from SE observed facts + provider/DOS/state.
* Bridge I3 reads this only — no consultation document reads.
*/
const bridgeServiceVendorTargetSchema = z$1.object({
	customerId: zodFlavorId(),
	consultationId: zodFlavorId(),
	serviceEligibilityVendorNativeId: zodFlavorId(),
	dateOfService: z$1.string().min(1),
	state: z$1.string().min(1),
	serviceTypeId: z$1.string().min(1),
	providerId: zodFlavorId()
});
/**
* Complete Bridge service bill target on consultations/vendors/bridge.
* Admin approve seeds canvas note pointer + envelope ids; Bridge I3 maps chart and POSTs.
*/
const bridgeServiceBillVendorTargetSchema = z$1.object({
	customerId: zodFlavorId(),
	consultationId: zodFlavorId(),
	serviceId: zodFlavorId(),
	providerId: zodFlavorId(),
	/** Canvas note UUID for plugin-io chart pull. */
	canvasNoteId: z$1.string().min(1),
	/** Consult booking slot length — not used for mapped note minutes (Time Spent SSOT). */
	durationMinutes: z$1.number().int().positive().optional()
});
function bridgeServiceBillVendorTargetReady(target) {
	return bridgeServiceBillVendorTargetSchema.safeParse(target).success;
}
function bridgeServiceBillVendorTargetsEqual(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return deepEqual(a, b);
}
const bridgeConsultationVendorTargetsSchema = z$1.object({
	serviceEligibility: bridgeServiceEligibilityVendorTargetSchema.optional(),
	service: bridgeServiceVendorTargetSchema.optional(),
	serviceBill: bridgeServiceBillVendorTargetSchema.optional()
});
function bridgeServiceBillVendorTargetChanged(before, after) {
	const beforeTarget = before?.serviceBill;
	const afterTarget = after?.serviceBill;
	if (!afterTarget) return false;
	if (!beforeTarget) return true;
	return !bridgeServiceBillVendorTargetsEqual(beforeTarget, afterTarget);
}
function bridgePaymentVendorTargetReady(target) {
	if (!target) return false;
	return Boolean(target.paymentKind) && Boolean(target.customerId) && Boolean(target.consultationId) && Boolean(target.serviceId) && Boolean(target.transactionId) && Number.isInteger(target.amount) && target.amount !== 0 && (target.paymentKind === "settling_refund" ? target.amount < 0 : target.amount > 0) && Boolean(target.paidAt) && Boolean(target.type);
}
function bridgePaymentVendorTargetsEqual(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return deepEqual(a, b);
}
const BRIDGE_EVENT_TYPE_PAYMENT_RESOLVE = "bridge.payment_resolve";
const bridgePaymentResolveEventDataSchema = z$1.object({
	consultationId: zodFlavorId(),
	customerId: zodFlavorId()
});
function bridgeServiceEligibilityVendorTargetReady(target) {
	if (!target) return false;
	return Boolean(target.customerId) && Boolean(target.consultationId) && Boolean(target.policyId) && Boolean(target.policyVendorNativeId) && Boolean(target.dateOfService) && Boolean(target.state) && Boolean(target.serviceTypeId) && Boolean(target.reservedSlotKey);
}
function bridgeServiceEligibilityVendorTargetsEqual(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return deepEqual(a, b);
}
function bridgeServiceEligibilityVendorTargetChanged(before, after) {
	const beforeTarget = before?.serviceEligibility;
	const afterTarget = after?.serviceEligibility;
	if (!afterTarget) return false;
	if (!beforeTarget) return true;
	return !bridgeServiceEligibilityVendorTargetsEqual(beforeTarget, afterTarget);
}
function bridgeServiceVendorTargetReady(target) {
	if (!target) return false;
	return Boolean(target.customerId) && Boolean(target.consultationId) && Boolean(target.serviceEligibilityVendorNativeId) && Boolean(target.dateOfService) && Boolean(target.state) && Boolean(target.serviceTypeId) && Boolean(target.providerId);
}
function bridgeServiceVendorTargetsEqual(a, b) {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return deepEqual(a, b);
}
function bridgeServiceVendorTargetChanged(before, after) {
	const beforeTarget = before?.service;
	const afterTarget = after?.service;
	if (!afterTarget) return false;
	if (!beforeTarget) return true;
	return !bridgeServiceVendorTargetsEqual(beforeTarget, afterTarget);
}
const BRIDGE_EVENT_TYPE_SERVICE_ELIGIBILITY_RESOLVE = "bridge.service_eligibility_resolve";
const bridgeServiceEligibilityResolveEventDataSchema = z$1.object({
	consultationId: zodFlavorId(),
	customerId: zodFlavorId()
});
const BRIDGE_EVENT_TYPE_SERVICE_RESOLVE = "bridge.service_resolve";
const bridgeServiceResolveEventDataSchema = z$1.object({
	consultationId: zodFlavorId(),
	customerId: zodFlavorId()
});
const BRIDGE_EVENT_TYPE_SERVICE_BILL_RESOLVE = "bridge.service_bill_resolve";
const bridgeServiceBillResolveEventDataSchema = z$1.object({
	consultationId: zodFlavorId(),
	customerId: zodFlavorId()
});
const BRIDGE_EVENT_TYPE_NOTE_EVALUATE = "bridge.note_evaluate";
/** Evaluate-without-create ingress — pointer ids only, no consult-doc reads. */
const bridgeNoteEvaluateEventDataSchema = z$1.object({
	consultationId: zodFlavorId(),
	customerId: zodFlavorId(),
	canvasNoteId: z$1.string().min(1),
	serviceId: zodFlavorId(),
	providerId: zodFlavorId(),
	/** Ignored by Bridge mapper after PR3 — minutes come from Canvas Time Spent. */
	durationMinutes: z$1.number().int().positive().optional()
});
/** Observed SE status poll metadata (mirror bridge policy statusPoll). */
const bridgeServiceEligibilityStatusPollKindSchema = z$1.enum(["create"]);
const bridgeServiceEligibilityStatusPollOutcomeSchema = z$1.enum(["timeout"]);
const bridgeServiceEligibilityStatusPollSchema = z$1.object({
	kind: bridgeServiceEligibilityStatusPollKindSchema,
	startedAt: firestoreDate,
	deadlineAt: firestoreDate,
	seriesId: z$1.string().min(1),
	outcome: bridgeServiceEligibilityStatusPollOutcomeSchema.optional()
});

export { BRIDGE_EVENT_TYPE_NOTE_EVALUATE, BRIDGE_EVENT_TYPE_PAYMENT_RESOLVE, BRIDGE_EVENT_TYPE_SERVICE_BILL_RESOLVE, BRIDGE_EVENT_TYPE_SERVICE_ELIGIBILITY_RESOLVE, BRIDGE_EVENT_TYPE_SERVICE_RESOLVE, BRIDGE_SYNC_TARGET_PAYMENTS, BRIDGE_SYNC_TARGET_SERVICE, BRIDGE_SYNC_TARGET_SERVICE_BILL, BRIDGE_SYNC_TARGET_SERVICE_ELIGIBILITY, bridgeConsultationVendorTargetsSchema, bridgeNoteEvaluateEventDataSchema, bridgePaymentKindSchema, bridgePaymentResolveEventDataSchema, bridgePaymentTypeSchema, bridgePaymentVendorTargetReady, bridgePaymentVendorTargetSchema, bridgePaymentVendorTargetsEqual, bridgeServiceBillResolveEventDataSchema, bridgeServiceBillVendorTargetChanged, bridgeServiceBillVendorTargetReady, bridgeServiceBillVendorTargetSchema, bridgeServiceBillVendorTargetsEqual, bridgeServiceEligibilityResolveEventDataSchema, bridgeServiceEligibilityStatusPollKindSchema, bridgeServiceEligibilityStatusPollOutcomeSchema, bridgeServiceEligibilityStatusPollSchema, bridgeServiceEligibilityVendorTargetChanged, bridgeServiceEligibilityVendorTargetReady, bridgeServiceEligibilityVendorTargetSchema, bridgeServiceEligibilityVendorTargetsEqual, bridgeServiceResolveEventDataSchema, bridgeServiceVendorTargetChanged, bridgeServiceVendorTargetReady, bridgeServiceVendorTargetSchema, bridgeServiceVendorTargetsEqual };
//# sourceMappingURL=consultSyncTargets.mjs.map